import { computed } from "vue";
import { useRouter } from "vue-router";
import { useRouteQueryParam } from "@/core/composables";
import { useModuleSettings } from "@/core/composables/useModuleSettings";
import {
  CATALOG_BARCODE_SCANNER_ENABLED_KEY,
  CATALOG_BARCODE_SEARCH_FIELDS_KEY,
  MODULE_ID_CATALOG,
} from "@/core/constants/modules";
import { QueryParamName } from "@/core/enums";
import { parseJsonStringArray, toFirstString } from "@/core/utilities";
import { ROUTES } from "@/router/routes/constants";
import type { Ref } from "vue";
import type { RouteLocationRaw } from "vue-router";

/**
 * The search results route a scanned code navigates to in exact match mode. The code travels as its own
 * query param (not as `q`), so the results page can turn it into the `barcode` filter term.
 */
function getBarcodeSearchRoute(value: string): RouteLocationRaw {
  return { name: ROUTES.SEARCH.NAME, query: { [QueryParamName.Barcode]: value } };
}

/** The setting holds a JSON array of index fields; an unreadable one must not switch to exact match. */
function hasBarcodeSearchFields(rawValue: unknown): boolean {
  return !!parseJsonStringArray(rawValue)?.length;
}

// A scan whose `search` event is owed. The event carries the results, so the results page sends it: the request
// that starts with this code takes it (see `takeUnreportedScan`), and a scan that does not navigate drops it.
let unreportedScan: string | undefined;

export function takeUnreportedScan(barcode: string): boolean {
  if (!barcode || barcode !== unreportedScan) {
    return false;
  }

  unreportedScan = undefined;

  return true;
}

function forgetUnreportedScan(barcode: string): void {
  if (unreportedScan === barcode) {
    unreportedScan = undefined;
  }
}

/**
 * A scanned code identifies one product, so a single hit opens it instead of a one-item list.
 * The decision is taken on the state captured when the request was issued, so a response for a code the
 * URL has moved on from, a result narrowed by facets/paging/sorting, or a second completion for the same
 * code cannot navigate.
 */
export function shouldOpenSingleBarcodeHit(request: {
  requestedBarcode: string;
  currentBarcode: string;
  wasNarrowed: boolean;
  redirectedBarcodes: ReadonlySet<string>;
  totalCount: number;
  itemCount: number;
}): boolean {
  const { requestedBarcode, currentBarcode, wasNarrowed, redirectedBarcodes, totalCount, itemCount } = request;

  return (
    !!requestedBarcode &&
    requestedBarcode === currentBarcode &&
    !wasNarrowed &&
    !redirectedBarcodes.has(requestedBarcode) &&
    totalCount === 1 &&
    itemCount === 1
  );
}

/**
 * Store-level barcode scanner configuration, shared by the desktop and mobile search bars.
 * A backend without the settings keeps today's behaviour: the scanner is shown and a scanned code is
 * searched as a full-text phrase.
 */
export function useBarcodeSearch(options: {
  /** The bar's input model — today's behaviour puts the code in it and runs a keyword search. */
  searchPhrase: Ref<string>;
  searchDropdownRef: Readonly<Ref<{ handleSearch: () => void } | null>>;
  /** What the bar binds to the dropdown's `hide` event — the exact path navigates without the dropdown. */
  hideSearchResults: () => void;
}) {
  const router = useRouter();
  const { getSettingValue } = useModuleSettings(MODULE_ID_CATALOG);

  const isScannerEnabled = computed(() => getSettingValue(CATALOG_BARCODE_SCANNER_ENABLED_KEY) !== false);

  function onBarcodeScanned(value: string): void {
    if (!value) {
      return;
    }

    if (hasBarcodeSearchFields(getSettingValue(CATALOG_BARCODE_SEARCH_FIELDS_KEY))) {
      // By design the code is NOT saved to the search history (it is not a keyword) and the lookup stays global
      // (no category scope).
      unreportedScan = value;
      options.hideSearchResults();
      // Without a navigation (the code is already on screen, or it failed) no request would take the report,
      // and a later one for this code (a sort change, Back) must not send it late.
      void router.push(getBarcodeSearchRoute(value)).then(
        (failure) => {
          if (failure) {
            forgetUnreportedScan(value);
          }
        },
        () => forgetUnreportedScan(value),
      );
      return;
    }

    options.searchPhrase.value = value;
    options.searchDropdownRef.value?.handleSearch();
  }

  return {
    isScannerEnabled,
    onBarcodeScanned,
  };
}

/**
 * The `q` the search bars show. A barcode lookup ignores `q` (the results page does not send it), so the bars
 * ignore a leftover one too: shown, it would hide the scanner, and Enter would search it instead of the code.
 */
export function useSearchPhraseInUrl() {
  const searchPhrase = useRouteQueryParam<string>(QueryParamName.SearchPhrase);
  const barcode = useRouteQueryParam<string>(QueryParamName.Barcode);

  // Decided as the results page decides a lookup (`isBarcodeLookup` in `useProducts`).
  return computed(() => (toFirstString(barcode.value) ? "" : searchPhrase.value));
}
